import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type LinksDisplayMode = "grouped" | "compact";
export type NotesDisplayMode = "grouped" | "compact";

/** Whether a string (a select's value, say) names a display mode. */
export const isDisplayMode = (
  value: string,
): value is LinksDisplayMode & NotesDisplayMode =>
  value === "grouped" || value === "compact";

export interface SettingsState {
  // Display settings
  linksDisplayMode: LinksDisplayMode;
  notesDisplayMode: NotesDisplayMode;
  showGroupHeaders: boolean;
  tileSize: "small" | "medium" | "large";

  // Other settings
  animations: boolean;
  soundEffects: boolean;
}

const initialState: SettingsState = {
  // Display settings - default to compact mode
  linksDisplayMode:
    (localStorage.getItem("linksDisplayMode") as LinksDisplayMode) || "compact",
  notesDisplayMode:
    (localStorage.getItem("notesDisplayMode") as NotesDisplayMode) || "compact",
  showGroupHeaders: true,
  tileSize:
    (localStorage.getItem("tileSize") as "small" | "medium" | "large") ||
    "medium",

  // Other settings
  animations: localStorage.getItem("animations") !== "false",
  soundEffects: localStorage.getItem("soundEffects") !== "false",
};

const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    setLinksDisplayMode: (state, action: PayloadAction<LinksDisplayMode>) => {
      state.linksDisplayMode = action.payload;
      localStorage.setItem("linksDisplayMode", action.payload);
    },
    setNotesDisplayMode: (state, action: PayloadAction<NotesDisplayMode>) => {
      state.notesDisplayMode = action.payload;
      localStorage.setItem("notesDisplayMode", action.payload);
    },
    setShowGroupHeaders: (state, action: PayloadAction<boolean>) => {
      state.showGroupHeaders = action.payload;
      localStorage.setItem("showGroupHeaders", action.payload.toString());
    },
    setTileSize: (
      state,
      action: PayloadAction<"small" | "medium" | "large">,
    ) => {
      state.tileSize = action.payload;
      localStorage.setItem("tileSize", action.payload);
    },
    setAnimations: (state, action: PayloadAction<boolean>) => {
      state.animations = action.payload;
      localStorage.setItem("animations", action.payload.toString());
    },
    setSoundEffects: (state, action: PayloadAction<boolean>) => {
      state.soundEffects = action.payload;
      localStorage.setItem("soundEffects", action.payload.toString());
    },
    resetSettings: (state) => {
      // Reset to defaults
      state.linksDisplayMode = "compact";
      state.notesDisplayMode = "compact";
      state.showGroupHeaders = true;
      state.tileSize = "medium";
      state.animations = true;
      state.soundEffects = true;

      // Clear localStorage
      localStorage.removeItem("linksDisplayMode");
      localStorage.removeItem("notesDisplayMode");
      localStorage.removeItem("showGroupHeaders");
      localStorage.removeItem("tileSize");
      localStorage.removeItem("animations");
      localStorage.removeItem("soundEffects");
    },
  },
});

export const {
  setLinksDisplayMode,
  setNotesDisplayMode,
  setShowGroupHeaders,
  setTileSize,
  setAnimations,
  setSoundEffects,
  resetSettings,
} = settingsSlice.actions;

export default settingsSlice.reducer;
